import { useState, useEffect, useRef, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { analytics } from "@/lib/analytics";

const TRUST_MESSAGES = [
  { text: "97% Reorder Rate", emoji: "🔥" },
  { text: "72h Delivery", emoji: "⚡" },
  { text: "250+ Businesses", emoji: "🚀" },
  { text: "850K+ Views", emoji: "👁️" },
];

const StickyMobileCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [trustIndex, setTrustIndex] = useState(0);
  const [isEntering, setIsEntering] = useState(false);
  const lastScrollY = useRef(0);

  // Rotate trust messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setTrustIndex((prev) => (prev + 1) % TRUST_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroHeight = 600;
      const footerElement = document.querySelector('footer');
      const footerTop = footerElement?.getBoundingClientRect().top || Infinity;
      
      // Calculate scroll progress (0-100)
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min((scrollPosition / documentHeight) * 100, 100);
      setScrollProgress(progress);
      
      // Show/hide logic: hide when scrolling down, show when scrolling up
      const scrollingDown = scrollPosition > lastScrollY.current;
      const scrollDelta = Math.abs(scrollPosition - lastScrollY.current);
      
      if (scrollDelta > 5) {
        setIsHidden(scrollingDown && scrollPosition > heroHeight);
      }
      
      lastScrollY.current = scrollPosition;
      
      // Main visibility: show after hero, hide before footer
      const shouldBeVisible = scrollPosition > heroHeight && footerTop > 100;
      
      if (shouldBeVisible && !isVisible) {
        setIsEntering(true);
        setTimeout(() => setIsEntering(false), 500);
      }
      
      setIsVisible(shouldBeVisible);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible]);

  const handleOrderClick = () => {
    analytics.ctaClicked('get_my_content', 'sticky_mobile_cta');
    window.location.href = "/order";
  };

  // SVG progress ring
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  const currentTrust = useMemo(() => TRUST_MESSAGES[trustIndex], [trustIndex]);

  if (!isVisible) return null;

  return (
    <div 
      className={`md:hidden fixed bottom-0 left-0 right-0 z-40 pb-safe transition-all duration-300 ${
        isHidden ? 'translate-y-full' : 'translate-y-0'
      } ${isEntering ? 'animate-cta-entrance' : ''}`}
    >
      {/* Enhanced glassmorphism container */}
      <div className="relative overflow-hidden bg-background/80 backdrop-blur-xl border-t border-neon-purple/30 shadow-[0_-8px_32px_rgba(168,85,247,0.25)]">
        {/* Animated gradient border top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-neon-purple via-neon-pink to-neon-cyan animate-gradient-shift" />
        
        {/* Subtle background glow */}
        <div className="absolute inset-0 bg-gradient-to-t from-neon-purple/5 to-transparent pointer-events-none" />
        
        <div className="relative p-3">
          {/* Trust ticker */}
          <div className="flex justify-center mb-2">
            <div 
              key={trustIndex}
              className="flex items-center gap-1.5 text-[11px] font-medium text-white/80 animate-trust-ticker-fade"
            >
              <span className="animate-flame-flicker">{currentTrust.emoji}</span>
              <span>{currentTrust.text}</span>
            </div>
          </div>
          
          {/* Main CTA row */}
          <div className="flex items-center gap-3">
            {/* Progress Ring */}
            <div className="relative w-11 h-11 flex-shrink-0">
              <svg className="w-11 h-11 -rotate-90" viewBox="0 0 44 44">
                {/* Background circle */}
                <circle
                  cx="22"
                  cy="22"
                  r="18"
                  stroke="hsl(var(--muted))"
                  strokeWidth="3"
                  fill="none"
                  className="opacity-30"
                />
                {/* Progress circle */}
                <circle
                  cx="22"
                  cy="22"
                  r="18"
                  stroke="url(#progressGradient)"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-300 ease-out drop-shadow-[0_0_6px_rgba(168,85,247,0.5)]"
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="hsl(var(--neon-purple))" />
                    <stop offset="100%" stopColor="hsl(var(--neon-pink))" />
                  </linearGradient>
                </defs>
              </svg>
              {/* Center percentage */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[10px] font-bold text-white/70">
                  {Math.round(scrollProgress)}%
                </span>
              </div>
            </div>
            
            {/* CTA Button */}
            <button
              onClick={handleOrderClick}
              className="
                flex-1
                px-5 py-3.5
                bg-gradient-to-r from-orange-500 via-neon-pink to-orange-600
                text-white
                rounded-full
                font-heading font-bold text-[15px]
                transition-all duration-200
                hover:opacity-95
                active:scale-[0.98]
                flex items-center justify-center gap-2
                touch-manipulation
                min-h-[48px]
                shadow-[0_4px_20px_rgba(249,115,22,0.4)]
                animate-glow-pulse-cta
                tap-feedback
              "
            >
              <span className="animate-flame-flicker">🚀</span>
              <span>Get My Content</span>
              <ArrowRight className="w-4 h-4 animate-bounce-x" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyMobileCTA;
